/**
 * CGST + SGST or IGST on a purchase (QA PU1-004, docs/purchase-gaps-plan.md §5.2).
 *
 * The API resolves the supply type (`supply` on a PO / vendor bill, or
 * `GET /purchase-orders/supply-preview` in the wizard); this file only splits
 * the stored tax by it — the mirror of the API `buildTaxTotalLines`: per line
 * SGST = floor(tax / 2), CGST = tax − SGST (CGST takes the odd paisa), IGST for
 * an inter-state supply or an import. NONE (a VAT tenant) has no split: the
 * screens keep their single tax figure.
 */
export type PoSupplyType = 'INTRA' | 'INTER' | 'IMPORT' | 'NONE';

export interface PoSupply {
  supply_type: PoSupplyType;
  supplier_state_code: string | null;
  receiver_state_code: string | null;
  /** DEFAULT = a side's state is unknown and intra-state was assumed. */
  source: 'GSTIN' | 'STATE' | 'DEFAULT';
  supplier_state?: string | null;
  receiver_state?: string | null;
}

export interface PoTaxLine {
  kind: 'CGST' | 'SGST' | 'IGST' | 'VAT';
  ratePct: number;
  /** minor units */
  amount: number;
}

/** True when the tax is shown split (an India GST tenant). */
export function isGstSplit(supply: PoSupply | null | undefined): supply is PoSupply {
  return !!supply && supply.supply_type !== 'NONE';
}

/** Tax lines of a set of PO / bill lines, grouped by rate in first-seen order; rate 0 left out. */
export function gstTaxLines(
  supply: PoSupply,
  items: ReadonlyArray<{ gst_rate: number; line_tax: number }>,
): PoTaxLine[] {
  const byRate = new Map<number, { tax: number; cgst: number; sgst: number }>();
  for (const item of items) {
    const acc = byRate.get(item.gst_rate) ?? { tax: 0, cgst: 0, sgst: 0 };
    const sgst = Math.floor(item.line_tax / 2);
    acc.tax += item.line_tax;
    acc.sgst += sgst;
    acc.cgst += item.line_tax - sgst;
    byRate.set(item.gst_rate, acc);
  }
  const lines: PoTaxLine[] = [];
  byRate.forEach(({ tax, cgst, sgst }, rate) => {
    if (rate === 0 || tax === 0) return;
    if (supply.supply_type === 'INTRA') {
      lines.push({ kind: 'CGST', ratePct: rate / 2, amount: cgst });
      lines.push({ kind: 'SGST', ratePct: rate / 2, amount: sgst });
    } else {
      lines.push({ kind: 'IGST', ratePct: rate, amount: tax });
    }
  });
  return lines;
}

/** "Tamil Nadu (33), intra-state" — or why it is not known. */
export function placeOfSupplyText(supply: PoSupply): string {
  if (supply.supply_type === 'IMPORT') return 'Import (IGST)';
  if (supply.source === 'DEFAULT') return 'Place of supply not set — CGST + SGST assumed. Add the supplier’s GSTIN or state.';
  const kind = supply.supply_type === 'INTRA' ? 'intra-state (CGST + SGST)' : 'inter-state (IGST)';
  return `Supplier in ${supply.supplier_state ?? supply.supplier_state_code} (${supply.supplier_state_code}), ${kind}`;
}
